import React, { useRef, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Paper, Button, Typography, Box } from "@material-ui/core";
import { useFormik } from "formik";
import useStyles from "@/styles/alarmStyle";
import SearchInput from "@/components/SearchInput";
import { useTranslation } from "@/langs/useTranslation";
import { toast } from "@/apis/request";
import { FormItem } from "@/components/Form";
import DateRangePicker from "@/components/DateRangePicker";
import {
  copyProfileConfig,
  deleteProfileConfig,
  getProfileConfigGroup,
  publishProfileConfig,
  setProfileConfigGroup,
  updateProfileConfigStatus
} from "@/apis/apiCollection";
import modalConfirm from "@/components/Modal/modalConfirm";
import SubmitButton from "@/components/commons/SubmitButton";
import { useGlobalState } from "@/GlobalProvider";
import HasPermission from "@/components/commons/HasPermission";
import SingleSelector from "@/components/SingleSelector";
import { ProfileTemplateListDataTable } from "./ProfileTemplateListDataTable";
import DropdownButton from "@/components/DropdownButton";
import { IStatus } from "../constant";
import ReleaseTimeModal from "../ReleaseTimeModal";
import DeviceGroupsModal from "@/components/FormFields/DeviceGroups/DeviceGroupsModal";
import { IconFile } from "@/components/Icon";
import { useDebounceFn } from "ahooks";

const ProfileTemplateList = () => {
  const { t } = useTranslation();
  const classes = useStyles();
  const tableRef = useRef(null);
  const selectedIdsRef = useRef([]);
  const isReset = useRef(true);
  const operateType = useRef(null);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [releaseTimeModalOpen, setReleaseTimeModalOpen] = useState(false);
  const [batchReleaseTimeModalOpen, setBatchReleaseTimeModalOpen] =
    useState(false);
  const [releaseId, setReleaseId] = useState();
  const [groupsModalOpen, setGroupsModalOpen] = useState();
  const [groupIds, setGroupIds] = useState();

  const initialValues = {
    osType: "",
    status: "",
    createTime: undefined,
    activeTime: undefined,
    updateTime: undefined,
    keywords: ""
  };

  const history = useHistory();
  const { currentCompanyId } = useGlobalState();

  const { values, handleChange, handleSubmit, handleReset } = useFormik({
    initialValues,
    onSubmit: () => {
      handleReloadDataTable();
    }
  });

  const getSearchParams = () => {
    const formValues = isReset.current ? initialValues : values;
    const { createTime, activeTime, updateTime } = formValues;
    return {
      osType: formValues.osType ? formValues.osType : "",
      status:
        formValues.status === "" ||
        (formValues.status !== 0 && !formValues.status)
          ? -1
          : formValues.status,
      keywords: formValues.keywords,
      createdAtStart: createTime ? createTime[0]?.valueOf() : null,
      createdAtEnd: createTime ? createTime[1]?.valueOf() : null,
      effectedAtStart: activeTime ? activeTime[0]?.valueOf() : null,
      effectedAtEnd: activeTime ? activeTime[1]?.valueOf() : null,
      updatedAtStart: updateTime ? updateTime[0]?.valueOf() : null,
      updatedAtEnd: updateTime ? updateTime[1]?.valueOf() : null
    };
  };

  const reloadTableWithGroupId = () => {
    isReset.current = false;
    tableRef.current?.reload();
  };

  const resetTableWithGroupId = () => {
    isReset.current = true;
    tableRef.current?.reload();
  };

  const handleReloadDataTable = reset => {
    if (reset) {
      resetTableWithGroupId();
    } else {
      reloadTableWithGroupId();
    }
  };

  const resetQuery = () => {
    handleReset();
    handleReloadDataTable(true);
  };

  const getSelectedIds = () => {
    const selectedIds = tableRef.current.getSelected();
    if (selectedIds && selectedIds.length) {
      selectedIdsRef.current = selectedIds;
      return selectedIds;
    }
    toast.info(t({ defaultMessage: "未选择任何模板" }));
  };

  const handleLoaded = json => {
    setTotal(json.recordsTotal);
  };

  const doEnable = async (ids, enable) => {
    try {
      setLoading(true);
      operateType.current = enable ? "enable" : "disable";
      if (!Array.isArray(ids)) {
        ids = [ids];
      }
      await updateProfileConfigStatus({
        status: enable ? 1 : 2,
        configIds: ids
      });
      handleReloadDataTable();
      toast.success(t({ defaultMessage: "操作成功" }));
    } finally {
      setLoading(false);
    }
  };

  const handleDisable = row => {
    modalConfirm({
      title: t({ defaultMessage: "禁用提醒" }),
      content: t(
        {
          defaultMessage:
            "是否确认禁用（{name}）该模板？禁用后设备无法引用该模板"
        },
        { name: row.name }
      ),
      onConfirm: () => {
        return doEnable(row.id, false);
      }
    });
  };

  const handleEnable = row => {
    modalConfirm({
      title: t({ defaultMessage: "启用提醒" }),
      content: t(
        {
          defaultMessage:
            "是否确认启用（{name}）该模板？启用后设备可以引用该模版"
        },
        { name: row.name }
      ),
      onConfirm: () => {
        return doEnable(row.id, true);
      }
    });
  };

  const doDelete = async ids => {
    try {
      setLoading(true);
      operateType.current = "delete";
      if (!Array.isArray(ids)) {
        ids = [ids];
      }
      await deleteProfileConfig({
        configIds: ids
      });
      handleReloadDataTable();
      toast.success(t({ defaultMessage: "删除成功" }));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = row => {
    modalConfirm({
      title: t({ defaultMessage: "删除提醒" }),
      content: t(
        {
          defaultMessage: "是否确认删除（{name}）该模板？"
        },
        { name: row.name }
      ),
      onConfirm: () => {
        return doDelete(row.id);
      }
    });
  };

  const handleBatchDisable = () => {
    if (getSelectedIds()) {
      modalConfirm({
        title: t({ defaultMessage: "提示" }),
        content: t({ defaultMessage: "确定要禁用吗？" }),
        onConfirm: () => {
          return doEnable(selectedIdsRef.current, false);
        }
      });
    }
  };

  const handleBatchEnable = () => {
    if (getSelectedIds()) {
      modalConfirm({
        title: t({ defaultMessage: "提示" }),
        content: t({ defaultMessage: "确定要启用吗？" }),
        onConfirm: () => {
          return doEnable(selectedIdsRef.current, true);
        }
      });
    }
  };

  const handleBatchRelease = async releasedAt => {
    await publishProfileConfig({
      configIds: selectedIdsRef.current,
      effectedAt: releasedAt
    });
    handleReloadDataTable();
    toast.success(t({ defaultMessage: "发布成功" }));
    setBatchReleaseTimeModalOpen(false);
  };

  const handleBatchDelete = () => {
    if (getSelectedIds()) {
      modalConfirm({
        title: t({ defaultMessage: "提示" }),
        content: t({ defaultMessage: "确定要删除吗？" }),
        onConfirm: () => {
          return doDelete(selectedIdsRef.current);
        }
      });
    }
  };

  const typeOptions = [
    { id: "", name: t({ defaultMessage: "全部" }) },
    { id: "windows", name: t({ defaultMessage: "Windows" }) },
    { id: "android", name: t({ defaultMessage: "Android" }) }
  ];

  const statusOptions = [
    { id: "", name: t({ defaultMessage: "全部" }) },
    { id: IStatus.DRAFT, name: t({ defaultMessage: "草稿" }) },
    { id: IStatus.ENABLE, name: t({ defaultMessage: "已发布" }) },
    { id: IStatus.DISABLE, name: t({ defaultMessage: "禁用" }) }
  ];

  // const onReleaseDateConfirm
  const { run: onReleaseDateConfirm } = useDebounceFn(
    async releasedAt => {
      await publishProfileConfig({
        configIds: [releaseId],
        effectedAt: releasedAt
      });
      handleReloadDataTable();
      toast.success(t({ defaultMessage: "发布成功" }));
      setReleaseTimeModalOpen(false);
    },
    { leading: true, trailing: false }
  );

  const onAction = (e, action, row) => {
    e.stopPropagation();
    switch (action) {
      case "edit":
        if (row.osType === "android") {
          history.push(`/profile-config/edit-template-android/${row.id}`);
        } else {
          history.push(`/profile-config/edit-template-windows/${row.id}`);
        }
        break;
      case "copy":
        modalConfirm({
          title: t({ defaultMessage: "提示" }),
          content: t({ defaultMessage: "确定要复制吗？" }),
          onConfirm: async () => {
            await copyProfileConfig({ configId: row.id });
            handleReloadDataTable();
          }
        });
        break;
      case "enable":
        handleEnable(row);
        break;
      case "disable":
        handleDisable(row);
        break;
      case "delete":
        handleDelete(row);
        break;
      case "release":
        setReleaseId(row.id);
        setReleaseTimeModalOpen(true);
        break;
      case "setGroup":
        setReleaseId(row.id);
        getProfileConfigGroup({ configId: row.id }).then(({ groupIds }) => {
          setGroupIds(groupIds);
          setGroupsModalOpen(true);
        });
        break;
    }
  };

  const handleGroupsConfirm = async (ids, detail, unInstallApp) => {
    try {
      await setProfileConfigGroup({
        configId: releaseId,
        groupIds: ids,
        remove_app: unInstallApp
      });
      setGroupsModalOpen(false);
      toast.success(t({ defaultMessage: "关联门店成功" }));
    } catch (e) {
      if (e.code === 50000) {
        toast.closeAll();
        toast.error(
          t({ defaultMessage: "当前已有配置文件关联门店，请检查。" })
        );
      }
    }
  };

  useEffect(() => {
    resetQuery();
  }, [currentCompanyId]);

  return (
    <div className={classes.main}>
      <Box display="flex" alignItems="center" marginBottom={1}>
        <Typography variant="h4">
          {t({ defaultMessage: "配置文件模板" })}
        </Typography>
      </Box>
      <Paper elevation={0}>
        <form onSubmit={handleSubmit}>
          <div className={classes.tableHeader}>
            <Box display="flex" marginBottom={1}>
              <span>
                <Typography variant="h5" component="span">
                  {t({ defaultMessage: "配置文件列表" })}
                </Typography>
                <Typography
                  variant="body2"
                  color="primary"
                  component="span"
                  className={classes.subTitle}
                >
                  <IconFile size={15} />
                  {t({ defaultMessage: "{num}条" }, { num: total })}
                </Typography>
              </span>
              <Box display="flex" alignItems="center" marginLeft="auto">
                <SearchInput
                  placeholder={t({ defaultMessage: "搜索" })}
                  name="keywords"
                  icon={null}
                  value={values.keywords}
                  onChange={handleChange}
                  style={{ marginRight: 16 }}
                />
                <HasPermission permission="profile_template_create">
                  <DropdownButton
                    title={t({ defaultMessage: "创建配置文件" })}
                    menu={[
                      {
                        name: "Android",
                        key: "Android",
                        onClick: () =>
                          history.push(
                            "/profile-config/create-template-android"
                          )
                      },
                      {
                        name: "Windows",
                        key: "Windows",
                        onClick: () =>
                          history.push(
                            "/profile-config/create-template-windows"
                          )
                      }
                    ]}
                  />
                </HasPermission>
              </Box>
            </Box>
            <div className={classes.tableQueryForm}>
              <FormItem
                label={t({ defaultMessage: "类型" })}
                className={classes.formItem}
              >
                <SingleSelector
                  placeholder={t({ defaultMessage: "全部" })}
                  options={typeOptions}
                  clearable
                  style={{ width: 120 }}
                  name="osType"
                  value={values.osType}
                  onChange={handleChange}
                />
              </FormItem>
              <FormItem
                label={t({ defaultMessage: "模板状态" })}
                className={classes.formItem}
              >
                <SingleSelector
                  placeholder={t({ defaultMessage: "全部" })}
                  options={statusOptions}
                  clearable
                  style={{ width: 120 }}
                  name="status"
                  value={values.status}
                  onChange={handleChange}
                />
              </FormItem>
              <FormItem
                label={t({ defaultMessage: "创建时间" })}
                className={classes.formItem}
              >
                <DateRangePicker
                  name="createTime"
                  value={values.createTime}
                  onChange={handleChange}
                />
              </FormItem>
              <FormItem
                label={t({ defaultMessage: "最后发布时间" })}
                className={classes.formItem}
              >
                <DateRangePicker
                  name="activeTime"
                  value={values.activeTime}
                  onChange={handleChange}
                />
              </FormItem>
              <FormItem
                label={t({ defaultMessage: "最后修改时间" })}
                className={classes.formItem}
              >
                <DateRangePicker
                  name="updateTime"
                  value={values.updateTime}
                  onChange={handleChange}
                />
              </FormItem>
              <FormItem className={classes.formItem}>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  style={{ marginRight: 8, width: 100 }}
                >
                  {t({ defaultMessage: "搜索" })}
                </Button>
                <Button
                  variant="outlined"
                  onClick={resetQuery}
                  style={{ width: 100 }}
                >
                  {t({ defaultMessage: "重置" })}
                </Button>
              </FormItem>
            </div>
          </div>
        </form>
        <ProfileTemplateListDataTable
          ref={tableRef}
          getSearchParams={getSearchParams}
          onAction={onAction}
          onLoaded={handleLoaded}
          footer={
            <>
              <HasPermission permission="alarm_policy_enable">
                <SubmitButton
                  variant="outlined"
                  color="primary"
                  type="button"
                  loading={loading && operateType.current === "disable"}
                  onClick={handleBatchDisable}
                >
                  {t({ defaultMessage: "批量禁用" })}
                </SubmitButton>
                <SubmitButton
                  variant="outlined"
                  color="primary"
                  type="button"
                  loading={loading && operateType.current === "enable"}
                  onClick={handleBatchEnable}
                >
                  {t({ defaultMessage: "批量启用" })}
                </SubmitButton>
              </HasPermission>
              <HasPermission permission="profile_template_edit">
                <SubmitButton
                  variant="outlined"
                  color="primary"
                  type="button"
                  loading={loading && operateType.current === "release"}
                  onClick={() => {
                    if (!getSelectedIds()) return;
                    // if (!(Array.isArray(selectedIdsRef.current) && selectedIdsRef.current.length)) {
                    //   return
                    // }
                    setBatchReleaseTimeModalOpen(true);
                  }}
                >
                  {t({ defaultMessage: "批量发布" })}
                </SubmitButton>
              </HasPermission>
              <HasPermission permission="alarm_policy_delete">
                <SubmitButton
                  variant="outlined"
                  color="primary"
                  type="button"
                  loading={loading && operateType.current === "delete"}
                  onClick={handleBatchDelete}
                >
                  {t({ defaultMessage: "批量删除" })}
                </SubmitButton>
              </HasPermission>
            </>
          }
        />
      </Paper>
      {releaseTimeModalOpen && (
        <ReleaseTimeModal
          open={releaseTimeModalOpen}
          onConfirm={onReleaseDateConfirm}
          onClose={() => setReleaseTimeModalOpen(false)}
        />
      )}
      {batchReleaseTimeModalOpen && (
        <ReleaseTimeModal
          open={batchReleaseTimeModalOpen}
          onConfirm={handleBatchRelease}
          onClose={() => setBatchReleaseTimeModalOpen(false)}
        />
      )}
      {groupsModalOpen && (
        <DeviceGroupsModal
          open={groupsModalOpen}
          checkedKeys={groupIds}
          checkStrictly
          disableRoot
          onClose={() => setGroupsModalOpen(false)}
          onConfirm={handleGroupsConfirm}
          dontCheck
        />
      )}
    </div>
  );
};

export default ProfileTemplateList;
